import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, MessageSquare, 
  Info, Navigation, Building,
  Wifi, MapPin, Heart, Share2, 
  ChevronLeft, ChevronRight, X,
  Calendar, Landmark, Tag,
  Maximize, DollarSign, FileText,
  ExternalLink
} from 'lucide-react';
import { Card } from '../components/ui/card';
import ContactSection from '../components/property/ContactSection';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/toast/use-toast';
import { ScrollRevealSection, ScrollReveal } from '../components/property/ScrollReveal';
import ScrollButton from '../components/property/ScrollButton';
import LocationSection from '../components/property/LocationSection';
import axios from 'axios';

// Constante para la URL base de imágenes de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Función para verificar correctamente si tiene unidades
const checkTieneUnidad = (valor) => {
  // Manejar diferentes formatos de datos booleanos
  if (valor === 1 || valor === '1' || valor === true || valor === 'true') {
    return true;
  }
  return false;
}

// Custom hook para el seguimiento del scroll
const useScrollObserver = () => {
  const [scrollDirection, setScrollDirection] = useState('down');
  const [scrollProgress, setScrollProgress] = useState(0);
  const prevScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determinar dirección del scroll
      if (currentScrollY > prevScrollY.current) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      
      // Calcular progreso del scroll (0-100%)
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (currentScrollY / scrollHeight) * 100;
      setScrollProgress(progress);
      
      prevScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { scrollDirection, scrollProgress };
};

// Función auxiliar para scroll suave
const scrollToElement = (elementId, offset = 0) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  // Calcular la posición del elemento
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;
  
  // Scroll suave hacia el elemento
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

// Componente para manejar imágenes con Cloudflare y manejo de errores
const PropertyImage = React.memo(({ url, alt, className }) => {
  const [error, setError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const previousUrl = useRef(url);

  // Función para obtener la URL completa
  const getFullUrl = useCallback((imageUrl) => {
    if (!imageUrl) return '/placeholder-property.jpg';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
  }, []);

  // URL completa memoizada
  const fullUrl = React.useMemo(() => getFullUrl(url), [url, getFullUrl]);

  // Efecto para manejar cambios de URL
  useEffect(() => {
    if (previousUrl.current !== url) {
      setIsLoaded(false);
      setError(false);
      previousUrl.current = url;
      
      // Log solo cuando cambia la URL
      //console.log('Loading new image:', {
        //originalUrl: url,
        //fullUrl: getFullUrl(url)
      //});
    }
  }, [url, getFullUrl]);

  const handleError = useCallback((e) => {
    if (!error) {
      console.error('Error loading image:', {
        originalUrl: url,
        fullUrl: fullUrl,
        error: e
      });
      setError(true);
    }
  }, [error, url, fullUrl]);

  const handleLoad = useCallback(() => {
    if (!isLoaded) {
      setIsLoaded(true);
      //console.log('Image loaded successfully:', fullUrl);
    }
  }, [isLoaded, fullUrl]);

  return (
    <img
      src={error ? '/placeholder-property.jpg' : fullUrl}
      alt={alt}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
});
PropertyImage.displayName = 'PropertyImage';

// Componente para mostrar la sección principal del proyecto
const MainProjectSection = ({ 
  proyecto, 
  formatPrice, 
  handleContact, 
  currentImageIndex, 
  handleFavorite, 
  handleShare, 
  handlePrevImage, 
  handleNextImage, 
  setCurrentImageIndex, 
  isFavorite,
  isImageExpanded,
  setIsImageExpanded,
  setIsPaused
}) => {
  const [hoverState, setHoverState] = useState({
    image: false,
    buttons: false
  });

  // Función auxiliar para procesar arrays
  const parseArrayValues = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return value.split(',').map(item => item.trim()).filter(Boolean);
      }
    }
    
    return [];
  };

  // Función para obtener los bancos de financiamiento
  const getBancos = () => {
    try {
      // Verificar si ya es un array
      if (Array.isArray(proyecto.bancos_financiamiento)) {
        return proyecto.bancos_financiamiento;
      }
      
      // Intentar parsear como JSON
      return JSON.parse(proyecto.bancos_financiamiento);
    } catch (error) {
      // Si no es JSON válido, tratar como string y dividir por comas
      return proyecto.bancos_financiamiento?.split(',').map(item => item.trim()) || [];
    }
  };

  // Componente de botón con icono
  const IconButton = ({ onClick, children, className = '' }) => (
    <motion.button 
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`relative group bg-white/90 backdrop-blur-sm p-3 rounded-xl
                shadow-lg transition-all duration-300 
                border border-white/50 hover:border-blue-200 ${className}`}
    >
      <div className="transition-transform duration-300 group-hover:scale-110">
        {children}
      </div>
    </motion.button>
  );

  // Componente de indicador de imagen
  const ImageIndicator = ({ index, isActive, onClick }) => (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`group relative h-2 transition-all duration-300 rounded-full overflow-hidden
                hover:scale-110 ${isActive ? 'w-8 bg-white' : 'w-2 bg-white/50'}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600
                    transition-opacity duration-300
                    ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}
      />
    </motion.button>
  );

  // Componente de botón de acción
  const ActionButton = ({ onClick, children, primary = false, icon: Icon = null }) => (
    <motion.button 
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`group relative overflow-hidden
        ${primary 
          ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white' 
          : 'bg-white text-gray-800'
        } 
        px-10 py-4 rounded-xl font-medium 
        transform transition-all duration-500 ease-out
        hover:shadow-2xl 
        w-full sm:w-auto border border-transparent
        ${primary ? 'hover:from-blue-700 hover:to-blue-800' : 'hover:border-blue-200'}`}
    >
      <div className="relative z-10 flex items-center justify-center gap-2">
        {Icon && <Icon className="w-5 h-5" />}
        <span className="transition-transform duration-300 group-hover:translate-x-1">
          {children}
        </span>
      </div>
    </motion.button>
  );

  // Clase dinámica para el título
  const titleSizeClass = proyecto.nombre_proyecto.length <= 30 
    ? 'text-4xl md:text-5xl lg:text-6xl' 
    : 'text-3xl md:text-4xl lg:text-5xl';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-20 pb-16 -mt-10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Sección de detalles */}
          <motion.div 
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="lg:flex lg:flex-col lg:justify-center h-full order-2 lg:order-1"
          >
            {/* Estado y Tipo del Proyecto */}
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">
                {proyecto.estado_proyecto === 'planos' && 'En Planos'}
                {proyecto.estado_proyecto === 'lanzamiento' && 'Lanzamiento'}
                {proyecto.estado_proyecto === 'construccion' && 'En Construcción'}
                {proyecto.estado_proyecto === 'terminado' && 'Terminado'}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {proyecto.tipo_proyecto === 'residencial' && 'Residencial'}
                {proyecto.tipo_proyecto === 'comercial' && 'Comercial'}
                {proyecto.tipo_proyecto === 'mixto' && 'Uso Mixto'}
              </span>
              <span className="text-sm text-gray-500">
                ID: {proyecto.id}
              </span>
            </div>

            {/* Título y fecha de entrega */}
            <div className="space-y-8">
              <motion.h1 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className={`${titleSizeClass} font-bold 
                           bg-gradient-to-r from-gray-900 to-gray-700 
                           bg-clip-text text-transparent
                           leading-tight`}
              >
                {proyecto.nombre_proyecto}
              </motion.h1>
            </div>

            {/* Tipos de unidades disponibles */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="border-t border-gray-200 py-4 my-4"
            >
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <Tag className="w-5 h-5 mr-2 text-blue-500" />
                Unidades desde:
              </h3>
              <div className="grid grid-cols-1 gap-y-4">
                {checkTieneUnidad(proyecto.tiene_1dorm) && (
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium">1 Dormitorio</p>
                      <p className="text-sm text-gray-500">{proyecto.area_1dorm} m² • {proyecto.unidades_1dorm} unidades</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-blue-600">S/ {formatPrice(proyecto.precio_1dorm_soles)}</p>
                      <p className="text-sm text-gray-500">$ {formatPrice(proyecto.precio_1dorm_dolares)}</p>
                    </div>
                  </div>
                )}
                
                {checkTieneUnidad(proyecto.tiene_2dorm) && (
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium">2 Dormitorios</p>
                      <p className="text-sm text-gray-500">{proyecto.area_2dorm} m² • {proyecto.unidades_2dorm} unidades</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-blue-600">S/ {formatPrice(proyecto.precio_2dorm_soles)}</p>
                      <p className="text-sm text-gray-500">$ {formatPrice(proyecto.precio_2dorm_dolares)}</p>
                    </div>
                  </div>
                )}
                
                {/* Solo mostramos 1 y 2 dormitorios en la vista principal para no ocupar tanto espacio */}
                {(checkTieneUnidad(proyecto.tiene_3dorm) || checkTieneUnidad(proyecto.tiene_4dorm)) && (
                  <button
                    onClick={() => {
                      const section = document.getElementById('caracteristicas');
                      section?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-1"
                  >
                    <span>Ver más tipos de unidades</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>

            {/* Financiamiento bancario si está disponible */}
            {proyecto.tiene_financiamiento === 'Si' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mb-5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Landmark className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold">Financiamiento Bancario</h3>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-2">
                  {getBancos().map((banco, index) => (
                    <span key={index} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                      {banco}
                    </span>
                  ))}
                  {proyecto.otro_banco && (
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                      {proyecto.otro_banco}
                    </span>
                  )}
                </div>
              </motion.div>
            )}
            
            {/* Botones de acción - Visible solo en pantallas móviles (debajo del financiamiento) */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex lg:hidden flex-col sm:flex-row gap-4 mt-8 mb-10"
            >
              <ActionButton 
                onClick={handleContact}
                primary
                icon={MessageSquare}
              >
                Contactar al Vendedor
              </ActionButton>
              <ActionButton 
                onClick={() => {
                  const section = document.getElementById('caracteristicas');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
                icon={Building}
              >
                Ver Características
              </ActionButton>
            </motion.div>

            {/* Este bloque ya no es necesario ya que los botones se mueven debajo del carrusel */}
          </motion.div>

          {/* Carrusel de imágenes */}
          <motion.div 
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="relative order-1 lg:order-2"
            onMouseEnter={() => {
              setHoverState(prev => ({ ...prev, image: true }));
              setIsPaused(true);
            }}
            onMouseLeave={() => {
              setHoverState(prev => ({ ...prev, image: false }));
              setIsPaused(false);
            }}
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl
                         transform transition-all duration-500 
                         hover:shadow-[0_20px_50px_rgba(8,_112,_184,_0.7)]">
              <AnimatePresence>
                <motion.div 
                  key={currentImageIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                  onClick={() => {
                    setIsImageExpanded(true);
                    setIsPaused(true);
                  }}
                  className="group cursor-zoom-in relative w-full h-full"
                >
                  {/* Overlays */}
                  <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/50 to-transparent z-10" />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent z-10" />

                  <PropertyImage
                    url={proyecto.imagenes[currentImageIndex]}
                    alt={`${proyecto.nombre_proyecto} - Imagen ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700
                              group-hover:scale-110"
                  />
                </motion.div>
              </AnimatePresence>

              {/* Botones de acción */}
              <div className={`absolute top-4 right-4 z-20 flex gap-3 
                          lg:transition-all lg:duration-300
                          ${hoverState.image 
                            ? 'lg:translate-y-0 lg:opacity-100' 
                            : 'lg:-translate-y-2 lg:opacity-0'
                          }
                          block opacity-100 translate-y-0 sm:opacity-100 sm:translate-y-0`}>
                <IconButton onClick={(e) => {
                  e.stopPropagation();
                  handleFavorite();
                }}>
                  <Heart className={`w-5 h-5 transition-all duration-300 
                                ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-700'}`} />
                </IconButton>
                <IconButton onClick={(e) => {
                  e.stopPropagation();
                  handleShare();
                }}>
                  <Share2 className="w-5 h-5 text-gray-700" />
                </IconButton>
              </div>

              {/* Navigation Buttons */}
              <div className={`absolute inset-y-0 left-4 flex items-center transition-all duration-300
                          ${hoverState.image ? 'opacity-100' : 'opacity-0'}`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage();
                  }}
                  className="p-2 bg-white/90 backdrop-blur-sm rounded-xl text-gray-800
                      transition-all duration-300 hover:scale-110 hover:-translate-x-1
                      border border-white/50 hover:border-blue-200"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              </div>

              <div className={`absolute inset-y-0 right-4 flex items-center transition-all duration-300
                          ${hoverState.image ? 'opacity-100' : 'opacity-0'}`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                  className="p-2 bg-white/90 backdrop-blur-sm rounded-xl text-gray-800
                      transition-all duration-300 hover:scale-110 hover:translate-x-1
                      border border-white/50 hover:border-blue-200"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Image Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {proyecto.imagenes.map((_, index) => (
                  <ImageIndicator
                    key={index}
                    index={index}
                    isActive={index === currentImageIndex}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                  />
                ))}
              </div>
            </div>
            
            {/* Botones de acción - Visibles en pantallas grandes (debajo del carrusel) */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="hidden lg:flex lg:flex-row gap-4 mt-8"
            >
              <ActionButton 
                onClick={handleContact}
                primary
                icon={MessageSquare}
              >
                Contactar al Vendedor
              </ActionButton>
              <ActionButton 
                onClick={() => {
                  const section = document.getElementById('caracteristicas');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
                icon={Building}
              >
                Ver Características
              </ActionButton>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// Componente para mostrar las amenidades y características del proyecto
const AmenidadesSection = ({ proyecto, showAll, toggleShowAll }) => {
  // Parsear amenidades con manejo de diferentes formatos
  const getAmenidades = () => {
    if (!proyecto.amenidades) return [];
    
    try {
      // Verificar si ya es un array
      if (Array.isArray(proyecto.amenidades)) {
        return proyecto.amenidades;
      }
      
      // Intentar parsear como JSON
      return JSON.parse(proyecto.amenidades);
    } catch (error) {
      // Si no es JSON válido, tratar como string y dividir por comas
      return proyecto.amenidades.split(',').map(item => item.trim());
    }
  };
  
  const amenidades = getAmenidades();
  
  return (
    <Card className="p-8 transform transition-all duration-500 hover:shadow-2xl">
      <div className="flex items-center gap-3 mb-6">
        <Wifi className="w-6 h-6 text-purple-500" />
        <h2 className="text-2xl font-semibold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
          Amenidades y Características
        </h2>
      </div>
      
      {amenidades.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {(showAll ? amenidades : amenidades.slice(0, 6)).map((amenidad, index) => (
            <div key={index} className="flex items-center gap-2 p-3 rounded-lg bg-purple-50">
              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
              <span className="text-gray-800">{amenidad}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 mb-6">No se han especificado amenidades para este proyecto.</p>
      )}
      
      {amenidades.length > 6 && (
        <button
          onClick={toggleShowAll}
          className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
        >
          {showAll ? 'Mostrar menos' : `Ver todas (${amenidades.length})`}
        </button>
      )}
      
      {proyecto.otras_caracteristicas && (
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold mb-3">Otras Características</h3>
          <p className="text-gray-600 whitespace-pre-line">
            {proyecto.otras_caracteristicas}
          </p>
        </div>
      )}
    </Card>
  );
};
// Componente principal ProyectDetails
const ProyectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  // Estados
  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllAmenidades, setShowAllAmenidades] = useState(false);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
  const [activeSection, setActiveSection] = useState('main');
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Referencias para secciones
  const mainRef = useRef(null);
  const aboutRef = useRef(null);
  const amenidadesRef = useRef(null);
  const locationRef = useRef(null);
  const contactRef = useRef(null);

  // Custom hooks
  const { scrollDirection, scrollProgress } = useScrollObserver();
  
  
  // Intersección observador para determinar sección activa
  useEffect(() => {
    if (!proyecto) return;

    const options = {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: 0.2
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    // Observar cada sección
    const sections = ['main', 'sobre', 'amenidades', 'ubicacion', 'contacto'];
    sections.forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => {
      sections.forEach(id => {
        const element = document.getElementById(id);
        if (element) observer.unobserve(element);
      });
    };
  }, [proyecto]);
  
  // Scrolling hacia seccion específica con efecto de centrado
  const scrollToSection = useCallback((sectionId) => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    // Calcular la posición para centrar la sección
    const viewportHeight = window.innerHeight;
    const elementHeight = section.getBoundingClientRect().height;
    const offset = Math.max(0, (viewportHeight - elementHeight) / 2);
    
    window.scrollTo({
      top: section.offsetTop - offset,
      behavior: 'smooth'
    });
  }, []);
  
  // Depurar los valores de tipos de unidades cuando se carga el proyecto
  //useEffect(() => {
  //  if (proyecto) {
  //    console.log('Tipos de unidad disponibles:', {
  //      tiene_1dorm: proyecto.tiene_1dorm, 
  //      tipo_1dorm: typeof proyecto.tiene_1dorm,
  //      tiene_2dorm: proyecto.tiene_2dorm,
  //      tipo_2dorm: typeof proyecto.tiene_2dorm,
  //      tiene_3dorm: proyecto.tiene_3dorm,
  //      tipo_3dorm: typeof proyecto.tiene_3dorm,
  //      tiene_4dorm: proyecto.tiene_4dorm,
  //      tipo_4dorm: typeof proyecto.tiene_4dorm
  //    });
  //  }
  //}, [proyecto]);
  
  // Función para obtener detalles del proyecto
  const fetchProyectoDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
  
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app/api';
      const endpoint = `${baseUrl}/proyectos/${id}`;
  
      //console.log('Fetching proyecto details:', { endpoint, id });

      const response = await axios.get(endpoint, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      const proyectoData = response.data;
      
      //console.log('Proyecto data received:', proyectoData);
      setProyecto(proyectoData);
      
      // Verificar estado de favoritos si hay usuario
      if (user && token) {
        await checkFavoriteStatus();
      }
      
    } catch (error) {
      console.error('Error fetching proyecto:', error);
      setError(error.response?.data?.error || 'No se pudo cargar el proyecto');
      toast({
        title: "Error",
        description: "No se pudo cargar el proyecto inmobiliario",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [id, token, toast, user]);
  
  // Verificar estado de favoritos
  const checkFavoriteStatus = useCallback(async () => {
    if (!user || !token) {
      setIsFavorite(false);
      return;
    }

    try {
      // Primero verificar si el endpoint acepta el parámetro tipo
      const response = await axios.get(
        `https://backend-production-1786.up.railway.app/api/favoritos/check/${id}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setIsFavorite(response.data.isFavorite);
    } catch (error) {
      console.error('Error checking favorite status:', error);
      toast({
        title: "Error",
        description: "No se pudo verificar el estado de favoritos",
        variant: "destructive",
      });
    }
  }, [id, user, token, toast]);
  
  // Manejar favoritos
  const handleFavorite = async () => {
    if (!user) {
      toast({
        title: "Inicia sesión",
        description: "Necesitas iniciar sesión para guardar favoritos",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    if (isLoadingFavorite) return;

    setIsLoadingFavorite(true);

    try {
      if (isFavorite) {
        await axios.delete(`https://backend-production-1786.up.railway.app/api/favoritos/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        toast({
          title: "Eliminado de favoritos",
          description: "El proyecto se eliminó de tus favoritos",
        });
        setIsFavorite(false);
      } else {
        await axios.post(`https://backend-production-1786.up.railway.app/api/favoritos/${id}`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        toast({
          title: "Añadido a favoritos",
          description: "El proyecto se añadió a tus favoritos",
        });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar favoritos",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFavorite(false);
    }
  };
  
  // Cargar datos al montar el componente
  useEffect(() => {
    fetchProyectoDetails();
  }, [fetchProyectoDetails]);
  
  // Funciones auxiliares
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleNextImage = () => {
    if (proyecto?.imagenes?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === proyecto.imagenes.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (proyecto?.imagenes?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? proyecto.imagenes.length - 1 : prev - 1
      );
    }
  };

  const handleShare = async () => {
    if (!proyecto) return;

    try {
      await navigator.share({
        title: proyecto.nombre_proyecto,
        text: `Mira este proyecto inmobiliario en InKayros: ${proyecto.nombre_proyecto}`,
        url: window.location.href
      });
    } catch (error) {
      //console.log('Error compartiendo:', error);
      if (error.name === 'NotSupportedError') {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: "Link copiado",
          description: "El enlace se ha copiado al portapapeles",
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudo compartir el proyecto",
          variant: "destructive",
        });
      }
    }
  };
  
  // Estados de carga y error
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex justify-center items-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error al cargar el proyecto</h2>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  if (!proyecto) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex justify-center items-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Proyecto no encontrado</h2>
          <button 
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }
  
  // Adaptar el proyecto para usar el componente CharacteristicsSection existente
  const adaptedProperty = {
    titulo: proyecto.nombre_proyecto,
    tipo: 'departamento',
    operacion: 'comprar',
    area_total: checkTieneUnidad(proyecto.tiene_1dorm) ? proyecto.area_1dorm : 
               (checkTieneUnidad(proyecto.tiene_2dorm) ? proyecto.area_2dorm : 
               (checkTieneUnidad(proyecto.tiene_3dorm) ? proyecto.area_3dorm : 
               (checkTieneUnidad(proyecto.tiene_4dorm) ? proyecto.area_4dorm : 0))),
    area_ocupada: checkTieneUnidad(proyecto.tiene_1dorm) ? proyecto.area_1dorm : 
                 (checkTieneUnidad(proyecto.tiene_2dorm) ? proyecto.area_2dorm : 
                 (checkTieneUnidad(proyecto.tiene_3dorm) ? proyecto.area_3dorm : 
                 (checkTieneUnidad(proyecto.tiene_4dorm) ? proyecto.area_4dorm : 0))),
    habitaciones: (checkTieneUnidad(proyecto.tiene_1dorm) ? 1 : 0) + 
                 (checkTieneUnidad(proyecto.tiene_2dorm) ? 1 : 0) + 
                 (checkTieneUnidad(proyecto.tiene_3dorm) ? 1 : 0) + 
                 (checkTieneUnidad(proyecto.tiene_4dorm) ? 1 : 0),
    banos: 1,
    precio_soles: checkTieneUnidad(proyecto.tiene_1dorm) ? proyecto.precio_1dorm_soles : 
                 (checkTieneUnidad(proyecto.tiene_2dorm) ? proyecto.precio_2dorm_soles : 
                 (checkTieneUnidad(proyecto.tiene_3dorm) ? proyecto.precio_3dorm_soles : 
                 (checkTieneUnidad(proyecto.tiene_4dorm) ? proyecto.precio_4dorm_soles : 0))),
    precio_dolares: checkTieneUnidad(proyecto.tiene_1dorm) ? proyecto.precio_1dorm_dolares : 
                   (checkTieneUnidad(proyecto.tiene_2dorm) ? proyecto.precio_2dorm_dolares : 
                   (checkTieneUnidad(proyecto.tiene_3dorm) ? proyecto.precio_3dorm_dolares : 
                   (checkTieneUnidad(proyecto.tiene_4dorm) ? proyecto.precio_4dorm_dolares : 0))),
    servicios_basicos: proyecto.tiene_financiamiento === 'Si' ? ['agua', 'luz', 'desague', 'internet'] : []
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div 
          className="h-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      
      {/* Scroll Button */}
      <ScrollButton />

      <div className="relative">
        {/* Main Section */}
        <div id="main" className="min-h-screen flex items-center justify-center pt-5">
          <MainProjectSection 
            proyecto={proyecto}
            formatPrice={formatPrice}
            handleContact={() => scrollToSection('contacto')}
            currentImageIndex={currentImageIndex}
            handleFavorite={handleFavorite}
            handleShare={handleShare}
            handlePrevImage={handlePrevImage}
            handleNextImage={handleNextImage}
            setCurrentImageIndex={setCurrentImageIndex}
            isFavorite={isFavorite}
            isImageExpanded={isImageExpanded}
            setIsImageExpanded={setIsImageExpanded}
            setIsPaused={setIsPaused}
          />
        </div>

        {/* About Section */}
        <div id="sobre" className="min-h-[70vh] flex items-center justify-center -mt-24">
          <ScrollRevealSection
            className="w-full"
            direction="up"
            distance={30}
            duration={0.6}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <Card className="p-8 transform transition-all duration-500 hover:shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Info className="w-6 h-6 text-blue-500" />
                  <h2 className="text-2xl font-semibold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    Sobre el Proyecto
                  </h2>
                </div>
                <p className="text-gray-600 whitespace-pre-line text-lg leading-relaxed">
                  {proyecto.descripcion_proyecto || 'No hay descripción disponible para este proyecto'}
                </p>
                
                {/* Resumen de unidades */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div>
                    <h3 className="text-md font-semibold text-blue-800 mb-2">Resumen del Proyecto</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>Total de unidades: {proyecto.total_unidades}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>Tipos de unidades: {[
                          checkTieneUnidad(proyecto.tiene_1dorm) ? '1 Dormitorio' : null,
                          checkTieneUnidad(proyecto.tiene_2dorm) ? '2 Dormitorios' : null,
                          checkTieneUnidad(proyecto.tiene_3dorm) ? '3 Dormitorios' : null,
                          checkTieneUnidad(proyecto.tiene_4dorm) ? '4 Dormitorios' : null
                        ].filter(Boolean).join(', ')}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>Estado: {
                          proyecto.estado_proyecto === 'planos' ? 'En Planos' :
                          proyecto.estado_proyecto === 'lanzamiento' ? 'Lanzamiento' :
                          proyecto.estado_proyecto === 'construccion' ? 'En Construcción' :
                          'Terminado'
                        }</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-md font-semibold text-blue-800 mb-2">Entrega</h3>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-700" />
                      <span>Fecha estimada: {proyecto.mes_entrega} {proyecto.anio_entrega}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 text-sm text-gray-500 flex items-center gap-2">
                  <span className="px-3 py-1 bg-blue-50 rounded-full text-blue-600">
                    Tiempo en esta página: {Math.floor(elapsedTime)} segundos
                  </span>
                </div>
              </Card>
            </div>
          </ScrollRevealSection>
        </div>

        {/* Características Section */}
        <div id="caracteristicas" className="min-h-[90vh] flex items-center justify-center -mt-10">
          <ScrollRevealSection
            className="w-full"
            direction="left"
            distance={50}
            duration={0.7}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="text-center mb-8">
                  <h2 className="inline-block text-3xl font-bold relative mb-2">
                    Tipos de Unidades
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full" />
                  </h2>
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    Conoce los diferentes tipos de unidades disponibles en este proyecto
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {checkTieneUnidad(proyecto.tiene_1dorm) && (
                    <Card className="p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                      <h3 className="text-xl font-bold text-blue-700 mb-3">1 Dormitorio</h3>
                      <div className="grid grid-cols-2 gap-y-3">
                        <div className="text-gray-600">Área:</div>
                        <div className="font-medium">{proyecto.area_1dorm} m²</div>
                        <div className="text-gray-600">Unidades:</div>
                        <div className="font-medium">{proyecto.unidades_1dorm}</div>
                        <div className="text-gray-600">Precio:</div>
                        <div className="font-medium text-blue-700">S/ {formatPrice(proyecto.precio_1dorm_soles)}</div>
                        <div className="text-gray-600"></div>
                        <div className="text-sm text-gray-500">$ {formatPrice(proyecto.precio_1dorm_dolares)}</div>
                      </div>
                    </Card>
                  )}
                  
                  {checkTieneUnidad(proyecto.tiene_2dorm) && (
                    <Card className="p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                      <h3 className="text-xl font-bold text-blue-700 mb-3">2 Dormitorios</h3>
                      <div className="grid grid-cols-2 gap-y-3">
                        <div className="text-gray-600">Área:</div>
                        <div className="font-medium">{proyecto.area_2dorm} m²</div>
                        <div className="text-gray-600">Unidades:</div>
                        <div className="font-medium">{proyecto.unidades_2dorm}</div>
                        <div className="text-gray-600">Precio:</div>
                        <div className="font-medium text-blue-700">S/ {formatPrice(proyecto.precio_2dorm_soles)}</div>
                        <div className="text-gray-600"></div>
                        <div className="text-sm text-gray-500">$ {formatPrice(proyecto.precio_2dorm_dolares)}</div>
                      </div>
                    </Card>
                  )}
                  
                  {checkTieneUnidad(proyecto.tiene_3dorm) && (
                    <Card className="p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                      <h3 className="text-xl font-bold text-blue-700 mb-3">3 Dormitorios</h3>
                      <div className="grid grid-cols-2 gap-y-3">
                        <div className="text-gray-600">Área:</div>
                        <div className="font-medium">{proyecto.area_3dorm} m²</div>
                        <div className="text-gray-600">Unidades:</div>
                        <div className="font-medium">{proyecto.unidades_3dorm}</div>
                        <div className="text-gray-600">Precio:</div>
                        <div className="font-medium text-blue-700">S/ {formatPrice(proyecto.precio_3dorm_soles)}</div>
                        <div className="text-gray-600"></div>
                        <div className="text-sm text-gray-500">$ {formatPrice(proyecto.precio_3dorm_dolares)}</div>
                      </div>
                    </Card>
                  )}
                  
                  {checkTieneUnidad(proyecto.tiene_4dorm) && (
                    <Card className="p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                      <h3 className="text-xl font-bold text-blue-700 mb-3">4 Dormitorios</h3>
                      <div className="grid grid-cols-2 gap-y-3">
                        <div className="text-gray-600">Área:</div>
                        <div className="font-medium">{proyecto.area_4dorm} m²</div>
                        <div className="text-gray-600">Unidades:</div>
                        <div className="font-medium">{proyecto.unidades_4dorm}</div>
                        <div className="text-gray-600">Precio:</div>
                        <div className="font-medium text-blue-700">S/ {formatPrice(proyecto.precio_4dorm_soles)}</div>
                        <div className="text-gray-600"></div>
                        <div className="text-sm text-gray-500">$ {formatPrice(proyecto.precio_4dorm_dolares)}</div>
                      </div>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </ScrollRevealSection>
        </div>

        {/* Amenidades Section */}
        <div id="amenidades" className="min-h-[90vh] flex items-center justify-center mt-10">
          <ScrollRevealSection
            className="w-full"
            direction="right"
            distance={50}
            duration={0.7}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <AmenidadesSection 
                proyecto={proyecto}
                showAll={showAllAmenidades}
                toggleShowAll={() => setShowAllAmenidades(!showAllAmenidades)}
              />
            </div>
          </ScrollRevealSection>
        </div>

        {/* Location Section */}
        <div id="ubicacion" className="min-h-screen flex items-center justify-center">
          <ScrollRevealSection
            className="w-full"
            direction="left"
            distance={50}
            duration={0.7}
          >
            <LocationSection property={{
              ...adaptedProperty,
              titulo: proyecto.nombre_proyecto,
              mapa_direccion: proyecto.mapa_direccion,
              ubicacion: proyecto.ubicacion,
              latitude: proyecto.latitude,
              longitude: proyecto.longitude,
              lugares_cercanos: []
            }} />
          </ScrollRevealSection>
        </div>

        {/* Contact Section */}
        <div id="contacto" className="min-h-screen flex items-center justify-center">
          <ScrollRevealSection
            className="w-full"
            direction="up"
            distance={50}
            duration={0.7}
          >
            <ContactSection 
              property={{
                ...adaptedProperty,
                titulo: proyecto.nombre_proyecto,
                id: proyecto.id,
                vendedor: proyecto.vendedor
              }}
              handleContact={() => {}}
              isRental={false}
            />
          </ScrollRevealSection>
        </div>
      </div>

      {/* Modal de imagen expandida */}
      <AnimatePresence>
        {isImageExpanded && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm
                      flex items-center justify-center"
            onClick={() => {
              setIsImageExpanded(false);
              setIsPaused(false);
            }}
          >
            {/* Botón de cierre */}
            <div className="absolute top-4 right-4 z-50 flex gap-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setIsImageExpanded(false);
                  setIsPaused(false);
                }}
                className="relative bg-white/10 backdrop-blur-sm p-3 rounded-xl
                          hover:bg-white/20 transition-all duration-300 
                          border border-white/30 shadow-lg"
              >
                <X className="w-6 h-6 text-white" />
              </motion.button>
            </div>

            {/* Contenedor de imagen */}
            <div className="relative w-full h-full max-w-7xl mx-auto p-4 flex items-center justify-center"
                 onClick={(e) => e.stopPropagation()}>
              <PropertyImage
                url={proyecto.imagenes[currentImageIndex]}
                alt={`${proyecto.nombre_proyecto} - Imagen ${currentImageIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              />
              
              {/* Navegación en modal */}
<motion.button
  whileHover={{ scale: 1.1 }}
  whileTap={{ scale: 0.9 }}
  onClick={(e) => {
    e.stopPropagation();
    handlePrevImage();
  }}
  className="absolute left-8 p-3 bg-white/10 backdrop-blur-sm rounded-xl text-white
          transition-all duration-300 hover:-translate-x-1 hover:bg-white/20"
>
  <ChevronLeft className="w-6 h-6" />
</motion.button>

<motion.button
  whileHover={{ scale: 1.1 }}
  whileTap={{ scale: 0.9 }}
  onClick={(e) => {
    e.stopPropagation();
    handleNextImage();
  }}
  className="absolute right-8 p-3 bg-white/10 backdrop-blur-sm rounded-xl text-white
          transition-all duration-300 hover:translate-x-1 hover:bg-white/20"
>
  <ChevronRight className="w-6 h-6" />
</motion.button>

{/* Indicadores de imagen */}
<div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
  {proyecto.imagenes.map((_, index) => (
    <button
      key={index}
      onClick={(e) => {
        e.stopPropagation();
        setCurrentImageIndex(index);
      }}
      className={`h-2 transition-all duration-300 rounded-full
                ${index === currentImageIndex 
                  ? 'w-8 bg-white' 
                  : 'w-2 bg-white/50 hover:bg-white/80'
                }`}
    />
  ))}
</div>
</div>
</motion.div>
)}
</AnimatePresence>
</div>
);
};

export default ProyectDetails;